import cx from 'classnames'
import { useButton } from '@react-aria/button'
import { useRef } from 'react'
import { useFocusRing } from '@react-aria/focus'

import Styles from './ReviewTemplates.module.scss'
import { ReviewTemplate } from 'types'
import { getInitials, pluralizeWithNumber } from 'utils'
import Avatar from 'common/Avatar/Avatar'
import { useHover } from '@react-aria/interactions'
import { EditButton } from './EditButton'
import { DeleteButton } from './DeleteButton'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { reviewTypeFromTemplate } from 'components/ReviewTemplateFormModal/ReviewTemplateForm/utils'
import { ReviewTypeIcon } from 'components/ReviewTypeIcon/ReviewTypeIcon'

export interface ReviewTemplateRowProps {
  template: ReviewTemplate
  onSelect?: (template: ReviewTemplate) => void
  showLastEditor?: boolean
}

export const ReviewTemplateRow = ({ template, onSelect, showLastEditor = false }: ReviewTemplateRowProps) => {
  const { hoverProps, isHovered } = useHover({})
  const lastEditor = showLastEditor && template.last_editor
  const iconButtonClass = cx({ [Styles.visible]: isHovered, [Styles.hidden]: !isHovered })
  const divRef = useRef(null)
  const { buttonProps, isPressed } = useButton({ elementType: 'div', onPress: () => onSelect?.(template) }, divRef)
  const rowHover = useHover({})
  const { isFocusVisible, focusProps } = useFocusRing()

  return (
    <ConditionalWrapper
      condition={!!onSelect}
      wrapper={(children) => (
        <div
          ref={divRef}
          key={template.id}
          className={cx(Styles.row, Styles.interactiveRow, {
            [Styles.withLastEditor]: !!lastEditor,
            [Styles.hovered]: rowHover.isHovered,
            [Styles.pressed]: isPressed,
            [Styles.focused]: isFocusVisible,
          })}
          role="button"
          aria-label={template.name}
          {...rowHover.hoverProps}
          {...buttonProps}
          {...focusProps}
        >
          {children}
        </div>
      )}
      elseWrapper={(children) => (
        <div className={cx(Styles.row, { [Styles.withLastEditor]: !!lastEditor })}>{children}</div>
      )}
    >
      <div className={Styles.flex} {...hoverProps}>
        <ReviewTypeIcon reviewType={reviewTypeFromTemplate(template)} size="sm" />
        <div className={Styles.templateName}>{template.name}</div>

        <div className={Styles.actions}>
          <EditButton template={template} className={iconButtonClass} />
          <DeleteButton template={template} className={iconButtonClass} />
        </div>
      </div>

      {lastEditor && (
        <div aria-label="last edited by" className={Styles.lastEditor}>
          <Avatar label={getInitials(lastEditor.display_name)} url={lastEditor.avatar_url} size={20} />
          <span>{lastEditor.display_name}</span>
        </div>
      )}

      <div aria-label="times used" className={Styles.timesUsed}>
        {pluralizeWithNumber(template.times_used, 'time')}
      </div>
    </ConditionalWrapper>
  )
}
